/**
 * useOfflineQueue — Story X1.3.3 (PRD X1 §FR-X1-9).
 *
 * Driver da fila de ações offline (`@/lib/offline-queue`). Montado 1× global
 * (via `<OfflineQueueIndicator/>` em App.tsx). Responsabilidades:
 *
 *   - Detecta online/offline (`navigator.onLine` + listeners).
 *   - Drena a fila ao reconectar (`online`) e como fallback iOS Safari
 *     (`visibilitychange` → visible + online), além do flush inicial no mount.
 *   - Registra os handlers por tipo de ação (send_message / mark_read /
 *     mark_all_read / react_feed_post), que fazem a chamada de rede real e
 *     reconciliam o cache do React Query.
 *   - Expõe `pendingCount` (poll 5s + pub/sub do queue) pro badge da UI.
 *
 * NÃO usa o SyncManager do Service Worker — tudo na thread principal, então
 * funciona em iOS Safari e sobre HTTP simples.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import api from "@/services/api";
import feedService from "@/services/feedService";
import { MESSAGES_KEY, ROOMS_KEY } from "@/hooks/useChat";
import type { ChatMessage, ChatMessageCreate } from "@/types/chat";
import {
  PermanentActionError,
  enqueue,
  flush,
  getCount,
  subscribe,
  type HandlerMap,
  type OfflineAction,
} from "@/lib/offline-queue";

const POLL_INTERVAL_MS = 5000;

export interface SendMessagePayload {
  room_id: string;
  body: ChatMessageCreate;
}
interface MarkReadPayload {
  id: string;
}
interface ReactFeedPayload {
  post_id: string;
  emoji: string;
}

/**
 * Executa uma chamada de API traduzindo o erro pro contrato do queue:
 * 4xx (≠429) → `PermanentActionError` (descarta); resto → rethrow (retry).
 */
async function runApiAction(fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn();
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    if (typeof status === "number" && status >= 400 && status < 500 && status !== 429) {
      throw new PermanentActionError(`HTTP ${status}`);
    }
    throw err; // 5xx / 429 / erro de rede → retry com backoff
  }
}

/** `true` se o erro do axios é de rede (sem `response` = request nunca chegou). */
export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  return (err as AxiosError).response === undefined;
}

/**
 * Mapa de handlers do flush. Cada handler faz a chamada real e reconcilia o
 * cache. Recebe o `QueryClient` (singleton) por parâmetro pra ser testável.
 */
export function buildOfflineHandlers(qc: QueryClient): HandlerMap {
  return {
    send_message: async (payload: SendMessagePayload, action: OfflineAction) => {
      await runApiAction(() =>
        api.post(`/chat/rooms/${payload.room_id}/messages`, payload.body, {
          // `Idempotency-Key` = id da ação: backend pode dedup quando ganhar o
          // middleware (mesma semântica da Story 2.7.3).
          headers: { "Idempotency-Key": action.id },
        })
      );
      // Remove a bolha otimista (id == action.id) e refetch da fonte real.
      qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(payload.room_id), (old) =>
        old ? old.filter((m) => m.id !== action.id) : old
      );
      void qc.invalidateQueries({ queryKey: MESSAGES_KEY(payload.room_id) });
      void qc.invalidateQueries({ queryKey: ROOMS_KEY });
    },
    mark_read: async (payload: MarkReadPayload) => {
      await runApiAction(() => api.patch(`/notifications/${payload.id}/read`));
    },
    mark_all_read: async () => {
      await runApiAction(() => api.post(`/notifications/mark-all-read`));
    },
    react_feed_post: async (payload: ReactFeedPayload) => {
      // Estado-desejado: só enfileiramos quando o POST original nunca saiu, então
      // um replay único do toggle atinge o estado pretendido (limitação conhecida:
      // janela de resposta-perdida — dedup avançado é diferido no PRD).
      await runApiAction(() => feedService.toggleReaction(payload.post_id, payload.emoji));
    },
  };
}

/**
 * Enfileira uma mensagem offline E injeta uma bolha otimista (`queued: true`)
 * no cache da sala. A bolha (cujo `id` == id da ação) ganha o ícone de relógio
 * no `MessageStatusIndicator` e é removida quando o flush reconcilia.
 */
export async function queueOfflineMessage(
  qc: QueryClient,
  roomId: string,
  author: { id: string; name?: string },
  data: ChatMessageCreate
): Promise<string> {
  const id = await enqueue<SendMessagePayload>("send_message", {
    room_id: roomId,
    body: data,
  });
  const optimistic: ChatMessage = {
    id,
    room_id: roomId,
    parent_message_id: data.parent_message_id ?? null,
    author_id: author.id,
    content: data.content,
    visibility_level: data.visibility_level ?? null,
    deleted_at: null,
    edited_at: null,
    created_at: new Date().toISOString(),
    author_name: author.name,
    queued: true,
  };
  qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
    old ? [...old, optimistic] : [optimistic]
  );
  return id;
}

export interface UseOfflineQueueResult {
  isOnline: boolean;
  pendingCount: number;
  flushNow: () => Promise<{ sent: number; failed: number }>;
}

export function useOfflineQueue(): UseOfflineQueueResult {
  const qc = useQueryClient();
  // Lazy init: `useRef(buildOfflineHandlers(qc))` reconstruiria o mapa a CADA
  // render (o argumento é avaliado sempre). Construímos uma única vez.
  const handlersRef = useRef<HandlerMap | undefined>(undefined);
  if (!handlersRef.current) {
    handlersRef.current = buildOfflineHandlers(qc);
  }
  const flushingRef = useRef(false);
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [pendingCount, setPendingCount] = useState(0);

  // qc é singleton estável; re-sincroniza só se mudar (não a cada render).
  useEffect(() => {
    handlersRef.current = buildOfflineHandlers(qc);
  }, [qc]);

  const refreshCount = useCallback(async () => {
    try {
      setPendingCount(await getCount());
    } catch (err) {
      console.debug("[useOfflineQueue] getCount falhou:", err);
    }
  }, []);

  const flushNow = useCallback(async () => {
    if (flushingRef.current) return { sent: 0, failed: 0 };
    flushingRef.current = true;
    try {
      const result = await flush(handlersRef.current ?? buildOfflineHandlers(qc));
      await refreshCount();
      return result;
    } finally {
      flushingRef.current = false;
    }
  }, [refreshCount, qc]);

  // online/offline + visibilitychange (fallback iOS sem Background Sync API)
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleOnline = () => {
      setIsOnline(true);
      void flushNow();
    };
    const handleOffline = () => setIsOnline(false);
    const handleVisibility = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible" &&
        navigator.onLine
      ) {
        void flushNow();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    // Flush inicial: app montou online com fila pendente de uma sessão anterior.
    if (navigator.onLine) void flushNow();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [flushNow]);

  // Contagem: poll leve + pub/sub do queue (atualiza badge em enqueue/flush).
  useEffect(() => {
    void refreshCount();
    const unsub = subscribe(() => {
      void refreshCount();
    });
    const handle = window.setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => {
      unsub();
      window.clearInterval(handle);
    };
  }, [refreshCount]);

  return { isOnline, pendingCount, flushNow };
}
